import { computePrayerTimes, type MethodId } from "./prayerTimes";

export interface SehriIftarDay {
  date: Date;
  sehriEnd: Date; // = Fajr (optionally minus precaution minutes)
  iftar: Date; // = Maghrib
  fastingMs: number;
}

export function sehriIftarFor(
  lat: number,
  lon: number,
  date: Date,
  method: MethodId = "Karachi",
  precautionMinutes = 0,
): SehriIftarDay {
  // Hanafi vs standard does not affect Fajr/Maghrib — keep default hanafi
  const t = computePrayerTimes(lat, lon, date, method, "hanafi");
  const sehriEnd = new Date(t.fajr.getTime() - precautionMinutes * 60_000);
  return {
    date,
    sehriEnd,
    iftar: t.maghrib,
    fastingMs: t.maghrib.getTime() - sehriEnd.getTime(),
  };
}

/** Hijri month/day/year via Umm al-Qura. */
export function getHijri(date: Date): { day: number; month: number; year: number } {
  try {
    const parts = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", {
      day: "numeric", month: "numeric", year: "numeric",
    }).formatToParts(date);
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
    return { day: get("day"), month: get("month"), year: get("year") };
  } catch {
    return { day: 0, month: 0, year: 0 };
  }
}

export function isRamadan(date: Date): boolean {
  return getHijri(date).month === 9;
}

/** Find the Gregorian date when Ramadan 1 (next or current) falls. */
export function findRamadanStart(from: Date = new Date()): Date {
  // Walk forward day-by-day up to ~13 months.
  const probe = new Date(from);
  probe.setHours(12, 0, 0, 0);
  for (let i = 0; i < 400; i++) {
    const h = getHijri(probe);
    if (h.month === 9 && h.day === 1) return new Date(probe);
    probe.setDate(probe.getDate() + 1);
  }
  return probe;
}

/** Generate full Ramadan month (1..29 or 30) for a location. */
export function ramadanMonthDays(
  lat: number,
  lon: number,
  method: MethodId = "Karachi",
  reference: Date = new Date(),
  precautionMinutes = 0,
): SehriIftarDay[] {
  // Find Ramadan 1 — current if inside, else next.
  let r1: Date;
  if (isRamadan(reference)) {
    const probe = new Date(reference);
    probe.setHours(12, 0, 0, 0);
    while (getHijri(probe).day !== 1) probe.setDate(probe.getDate() - 1);
    r1 = probe;
  } else {
    r1 = findRamadanStart(reference);
  }
  const out: SehriIftarDay[] = [];
  const cur = new Date(r1);
  for (let i = 0; i < 31; i++) {
    if (i > 0 && getHijri(cur).month !== 9) break;
    out.push(sehriIftarFor(lat, lon, new Date(cur), method, precautionMinutes));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}
